import { inspect, log } from '@/utils/logger'
import { requestPlaidLinkToken, plaidReportFetchState, startPlaidReportFetch, requestPlaidLinkUpdateToken, getPlaidPublicTokenReportState } from '@/services/api'
import assert from 'assert'
import store from '@/store'
import { PLAID_ANALYTICS_EVENT_PREFIX } from 'aven_types'

type OnPlaidSuccess = (plaidPublicToken: string, accounts: object[], institutionInfo: object | any, itemId: string | null) => {}
type PlaidLinkError = {
    error_type: string
    error_code: string
    error_message: string
    display_message: string
}
type PlaidLinkOnExitMetadata = {
    institution: null | {
        name: string
        institution_id: string
    }
    // see possible values for status at https://plaid.com/docs/link/web/#link-web-onexit-status
    status: null | string
    link_session_id: string
    request_id: string
}
type PlaidLinkOnExit = (error: null | PlaidLinkError, metadata: PlaidLinkOnExitMetadata) => void

type PlaidHandler = {
    error: ErrorEvent | null
    ready: boolean
    exit: () => any
    open: () => any
    // This one is psuedo-documented
    // https://plaid.com/docs/link/web/#destroy
    destroy: () => any
}

interface GetLinkTokenOptions {
    fromCache: boolean
}

const PLAID_SRC = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js'

export class PlaidManager {
    private plaidHandler?: PlaidHandler
    private readonly onPlaidSuccess: OnPlaidSuccess
    private readonly onPlaidExit: PlaidLinkOnExit
    private updateMode: boolean = false
    private institutionId: string | null = null

    constructor(onPlaidSuccess: OnPlaidSuccess, onPlaidExit: PlaidLinkOnExit) {
        this.onPlaidSuccess = onPlaidSuccess
        this.onPlaidExit = onPlaidExit
    }

    public init = async (updateMode: boolean = false, institutionId: string | null = null): Promise<boolean> => {
        log.info('bankConnect headless will initialize Plaid with updateMode: %{updateMode}, institutionId: %{institutionId}', { updateMode, institutionId })
        // Load the Plaid script async
        this.updateMode = updateMode
        this.institutionId = institutionId
        await this.loadPlaidScript()
        return await this.setupPlaid()
    }

    private loadPlaidScript = async (): Promise<void> => {
        for (const x of document.head.getElementsByTagName('script')) {
            if (x.src === PLAID_SRC) {
                log.info('Plaid already loaded')
                return
            }
        }
        log.info('Plaid script not found in DOM. Attaching...')

        return new Promise((resolve) => {
            const script = document.createElement('script')
            script.onload = () => {
                log.info('Plaid script loaded')
                resolve()
            }
            script.setAttribute('src', PLAID_SRC)
            log.info('Waiting for Plaid script to load...')
            document.head.appendChild(script)
        })
    }

    private getLinkToken = async (options: GetLinkTokenOptions): Promise<string | undefined> => {
        const { fromCache } = options
        try {
            if (fromCache) {
                log.info('Fetching link token from cache...')
                return await store.dispatch('getPlaidLinkToken')
            } else {
                log.info('Fetching new link token from Plaid API')
                let linkToken: string | undefined
                if (this.updateMode && this.institutionId) {
                    // We are in the normal iframe flow so we do not want to get a hosted link token
                    const response = await requestPlaidLinkUpdateToken(this.institutionId, false /* getHostedLinkToken */)
                    linkToken = response.data.payload.linkUpdateToken
                } else {
                    // We are in the normal iframe flow so we do not want to get a hosted link token
                    const response = await requestPlaidLinkToken(false /* getHostedLinkToken */)
                    linkToken = response.data.payload.linkToken
                }
                await store.dispatch('setPlaidLinkToken', linkToken)
                return linkToken
            }
        } catch (error) {
            log.fatal('Failed to fetch link token', { error })
        }
    }

    private setupPlaid = async (): Promise<boolean> => {
        try {
            let linkToken = await this.getLinkToken({ fromCache: true })
            const oauthStateId = this.getHasOAuthStateId()
            // We aren't allowed to carry extra query parameters with us into and out of the OAuth flow
            this.removeNonOauthQueryParams()
            if (linkToken && oauthStateId) {
                // This state happens when the user is redirected to the app having completed the oauth flow externally
                this.plaidHandler = this.createPlaidHandler(linkToken, this.onPlaidSuccess, this.onPlaidExit)
                this.plaidHandler.open()
            } else if (linkToken && !oauthStateId) {
                // This can occur if a user begins but fails out of the OAuth flow for one reason or another
                log.info('Detected linkToken in sessionStorage (%{linkToken}) but without an associated oauth_state_id, nuking linkToken from storage and getting a fresh link token', { linkToken })
                await store.dispatch('setPlaidLinkToken', null)
                linkToken = await this.getLinkToken({ fromCache: false })
                assert(linkToken, 'Should have a linkToken returned from getLinkToken()!')
                this.plaidHandler = this.createPlaidHandler(linkToken, this.onPlaidSuccess, this.onPlaidExit)
            } else if (!linkToken && !oauthStateId) {
                // Initial Plaid link state
                linkToken = await this.getLinkToken({ fromCache: false })
                assert(linkToken, 'Should have a linkToken returned from getLinkToken()!')
                this.plaidHandler = this.createPlaidHandler(linkToken, this.onPlaidSuccess, this.onPlaidExit)
            } else {
                // !linkToken && oauthStateId
                this.removeAllQueryParams()
                log.fatal('Detected an oauth state id in the query parameters without a cached link token! Check implementation.')
                linkToken = await this.getLinkToken({ fromCache: false })
                assert(linkToken, 'Should have a linkToken returned from getLinkToken()!')
                this.plaidHandler = this.createPlaidHandler(linkToken, this.onPlaidSuccess, this.onPlaidExit)
            }

            return true
        } catch (error) {
            log.fatal('Plaid initialize failed error:', { error })
        }
        return false
    }

    public open = (): void => {
        log.info('Opening plaid...')
        this.plaidHandler?.open()
    }

    public exit = (): void => {
        log.info('Making sure to exit plaid if its open...')
        this.plaidHandler?.exit()
    }

    private getHasOAuthStateId = (): boolean => {
        const queryParams = new URLSearchParams(window.location.search)
        return queryParams.has('oauth_state_id')
    }

    private removeNonOauthQueryParams = () => {
        const queryParams = new URLSearchParams(window.location.search)
        for (const [key] of queryParams) {
            if (key !== 'oauth_state_id') {
                queryParams.delete(key)
            }
        }

        window.history.replaceState(null, '', `${window.location.pathname}?${queryParams.toString()}`)
    }

    private removeAllQueryParams = () => {
        window.history.replaceState(null, '', window.location.pathname)
    }

    private cleanUpExistingPlaidIFrames = () => {
        // Always nuke any existing instances that are sticking around
        this.plaidHandler?.destroy()
        // This is necessary b/c sometimes plaid doesn't seem to delete itself fully (and it breaks various cypress tests)
        const elements = document.getElementsByTagName('iframe')
        // It's necessary to cache elements.length b/c it will reduce in size as we delete HTML elements from it
        for (let i = 0, len = elements.length; i != len; ++i) {
            elements[0].parentNode?.removeChild(elements[0])
        }
    }

    private createPlaidHandler = (linkToken: string, onSuccessHandler: OnPlaidSuccess, onExitHandler: PlaidLinkOnExit): PlaidHandler => {
        this.cleanUpExistingPlaidIFrames()

        // Our OAuth flow will automagically redirect us back to the same page we're on
        // Once redirected, we will need to pass our new query param ('oauth_state_id') back to the Plaid SDK
        const hasOAuthStateId = this.getHasOAuthStateId()

        const options = {
            token: linkToken,
            receivedRedirectUri: hasOAuthStateId ? window.location.href : undefined,
            onEvent: function (eventName: string, metadata: object) {
                log.info('plaid link onEvent metaData: %{metadataJson}', { metadataJson: JSON.stringify(metadata) })
                // log all events provided by plaid. note that these won't trigger until the plaid screen is closed.
                const internalEventName = `${PLAID_ANALYTICS_EVENT_PREFIX}${eventName.toLowerCase()}`
                window.logEvent(internalEventName, metadata)
            },
            onSuccess: function (publicToken: string, metadata: any) {
                window.logEvent('plaid_on_success', { ['plaid_bank_name']: metadata.institution.name })
                log.info('plaid link onSuccess metaData: %{metadataJson}', { metadataJson: JSON.stringify(metadata) })
                onSuccessHandler(publicToken, metadata.accounts, metadata.institution, null /* itemId */)
            },
            onExit: (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
                if (error) {
                    log.info('plaid link onExit returned error: %{errorJson}', { errorJson: JSON.stringify(error) })
                    window.logEvent(`${PLAID_ANALYTICS_EVENT_PREFIX}error`, { ...error, ...metadata })
                }

                log.info('plaid link onExit metaData: %{metadataJson}', { metadataJson: JSON.stringify(metadata) })

                // Always clear out the OAuth query parameters if there's an error, otherwise user will get stuck in a loop
                this.removeAllQueryParams()

                onExitHandler(error, metadata)
            },
        }

        log.info('Loading plaid link with the following options: %{optionsInspect}', { optionsInspect: inspect(options) })
        const handler = window.Plaid.create(options) as unknown as PlaidHandler
        if (!['production'].includes(process.env.VUE_APP_NODE_ENV as string)) {
            window.plaidOptions = options
            window.plaidHandler = handler
        }
        // This is a slight difference between the plaid-link-react and plaid-javascript libraries (typing wise)
        return handler
    }

    public static completePlaidFetch = async (plaidPublicToken: string) => {
        try {
            const response = await startPlaidReportFetch(plaidPublicToken)

            // trigger side effects of plaid authentication
            await store.dispatch('getIsPlaidAuthenticatedForSweepstakes')

            log.info('PlaidReport fetch started with response: %{responseInspect}', { responseInspect: inspect(response) })
            return {
                itemId: response.data.payload.itemId,
                institutionName: response.data.payload.institutionName,
                institutionId: response.data.payload.institutionId,
            }
        } catch (error) {
            log.fatal('Failed to start plaid report fetch', { error })
        }
        return {
            itemId: undefined,
            institutionName: undefined,
            institutionId: undefined,
        }
    }

    public static pollForPlaidReportCompletion = async (itemId: string): Promise<boolean> => {
        let retryCount = 0
        log.info('Beginning to poll for plaidReport fetch completion with itemId: %{itemId}', { itemId })
        while (retryCount < 150) {
            // ~ 2000ms * 150 retries = poll for 5 min
            const plaidReportState = await plaidReportFetchState(itemId)

            if (!plaidReportState.data.payload.isFetched) {
                retryCount++
                log.info('PlaidReport NOT READY, attempt#: %{retryCount}', { retryCount })
                await new Promise((r) => setTimeout(r, 2_000))
                continue
            }
            log.info('PlaidReport fetch is complete!')

            // Plaid Asset Fetch Status is set to StatusCodes.OK.toString() only if the asset report
            // fetch was successful.
            return plaidReportState.data.payload.isFetched
        }
        log.fatal('PlaidReport fetch did not appear to complete successfully!')
        return false
    }

    public static pollForPlaidPublicToken = async (
        plaidLinkToken: string
    ): Promise<
        | {
              success: true
              // if session completes, but customer exits, there will be no public token
              publicToken: string | undefined
          }
        | { success: false; publicToken: undefined }
    > => {
        let retryCount = 0
        log.info('Beginning to poll for plaid link token connection completion')
        while (retryCount < 150) {
            // ~ 2000ms * 150 retries = poll for 5 min
            const plaidReportState = await getPlaidPublicTokenReportState(plaidLinkToken)

            if (!plaidReportState.data.payload.plaidReportId) {
                retryCount++
                log.info('PlaidReport for plaid link token connection NOT READY, attempt#: %{retryCount}', { retryCount })
                await new Promise((r) => setTimeout(r, 2000))
                continue
            }

            log.info('PlaidReport for plaid link token connection session is complete!')
            return {
                success: true,
                publicToken: plaidReportState.data.payload.publicToken,
            }
        }
        log.error('PlaidReport for plaid link token connection did not appear to complete successfully!')
        return { success: false, publicToken: undefined }
    }
}
